import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updateLoginTime } from '../shared/actions/userActions';
import { AUTH_TOKEN_KEYS, ROLE } from '../shared/constants/common';
import { getLocalItem } from '../shared/utils/config/localStorage';
import { isLoginWithSupportPassword } from '../shared/utils/function';

/** Only PATCH when returning after this long hidden (avoids calls on quick tab switches). */
const MIN_HIDDEN_MS_BEFORE_SYNC = 6 * 60 * 60 * 1000; // 6 hours

function lastLoginTimeSyncIsStale() {
  const last = getLocalItem(AUTH_TOKEN_KEYS.LOGIN_TIME_LAST_SYNC_AT, null);
  if (last == null) return true;
  const lastMs = typeof last === 'number' ? last : parseInt(String(last), 10);
  if (Number.isNaN(lastMs)) return true;
  return Date.now() - lastMs >= MIN_HIDDEN_MS_BEFORE_SYNC;
}

/**
 * Keeps backend last-login time in sync for persisted sessions:
 * - On load only if last successful sync was long ago (avoids PATCH on every hard refresh).
 * - When the tab becomes visible again after being hidden for MIN_HIDDEN_MS_BEFORE_SYNC.
 */
function LoginTimeInitializer() {
  const dispatch = useDispatch();
  const lastHiddenAtRef = useRef(null);

  useEffect(() => {
    const role = Number(getLocalItem('role', null));
    if (role === ROLE.ADMIN || isLoginWithSupportPassword()) return;

    const token = localStorage.getItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN);
    if (token && lastLoginTimeSyncIsStale()) {
      dispatch(updateLoginTime());
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = lastHiddenAtRef.current;
      if (hiddenAt == null) return;
      if (Date.now() - hiddenAt < MIN_HIDDEN_MS_BEFORE_SYNC) return;
      lastHiddenAtRef.current = null;
      const t = localStorage.getItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN);
      if (t && role !== ROLE.ADMIN && !isLoginWithSupportPassword()) {
        dispatch(updateLoginTime());
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [dispatch]);

  return null;
}

export default LoginTimeInitializer;
